// Sidebar redesign — breadcrumb override context.
//
// The Breadcrumb component renders a default segment hierarchy from the
// current pathname (e.g. `/campaigns/abc` → `Campaigns › Detail`).
// Detail pages can swap the trailing segment for a domain label
// (e.g. the campaign's name) by mounting `<BreadcrumbOverride label="…"/>`
// — the override is set on mount and cleared on unmount.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface BreadcrumbContextValue {
  override: string | null;
  setOverride: (label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [override, setOverrideState] = useState<string | null>(null);
  const setOverride = useCallback((label: string | null) => {
    setOverrideState(label && label.length > 0 ? label : null);
  }, []);
  const value = useMemo<BreadcrumbContextValue>(
    () => ({ override, setOverride }),
    [override, setOverride],
  );
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

export function useBreadcrumb(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    // Outside a BreadcrumbProvider we degrade silently — the override is a
    // nice-to-have on top of the default path-derived breadcrumb.
    return { override: null, setOverride: () => undefined };
  }
  return ctx;
}

/**
 * Drop into a page that knows a better label than the default — e.g.
 * `<BreadcrumbOverride label={campaign.name} />` inside CampaignDetailPage.
 * Cleans up on unmount; safe to render conditionally.
 */
export const BreadcrumbOverride: React.FC<{ label: string | null | undefined }> = ({ label }) => {
  const { setOverride } = useBreadcrumb();
  // Capture the latest setter without re-running the effect on context churn.
  const setterRef = useRef(setOverride);
  useEffect(() => {
    setterRef.current = setOverride;
  }, [setOverride]);

  useEffect(() => {
    if (label && label.length > 0) {
      setterRef.current(label);
    }
    return () => {
      setterRef.current(null);
    };
  }, [label]);

  return null;
};
